import {FetchPolicy, type Result} from "@khanacademy/wonder-blocks-data";
import {isTruthy} from "@khanacademy/wonder-stuff-core";
import {useQuery} from "@khan/data-gql";
import Log, {Errors, KAError} from "@khan/logging";
import {RequestInfo} from "@khan/shared-core";

import {useLearnMenuTopicsQuery} from "#page-package/courses-menu/learn-menu-topics-query.ts";

import type {
    getIndexedContentSearchResults_searchPage_results_curationNode as CurationNode,
    getIndexedContentSearchResults_searchPage_results_learnableContent as LearnableContent,
} from "./__graphql-types__/getIndexedContentSearchResults.ts";
import {SearchableContentKinds} from "./constants.ts";
import {INDEXED_CONTENT_SEARCH_QUERY} from "./indexed-content-search-query.ts";

// We show 10 search results per page, querying for 50 results at a time.
// Which we'll sort to present in regionally relevant order.
export const QUERY_SEARCH_RESULTS = 50;
export const NUM_RESULTS_PER_PAGE = 10;
// Our logs indicate that less than 1% of users click next to see the 5th
// page of results. Querying for 5 pages of results will include most of
// the globally relevant matches, which we can reorder for the user region.
const NUM_QUERY_PAGES = QUERY_SEARCH_RESULTS / NUM_RESULTS_PER_PAGE;

// The AI Activities course is a special case. It exists to hold published
// Learner activites for all curations.
const AI_ACTIVITIES_SLUG = "ai-activities";
const AI_ACTIVITIES_ID = "x1b615e2cf0b0f17f";

/**
 * This represents a path to a content item published in a course.
 */
type ContentPathInCourse = {
    /**
     * The icon path of the domain in which the content item is published.
     */
    domainIconPath: string;

    /**
     * The course content ID in which the content item is published.
     */
    courseId: string;

    /**
     * The translated title of the course.
     */
    courseTitle: string;

    /**
     * The curriculum key, e.g. "us-cc", of the course.
     */
    courseCurriculum: string;

    /**
     * The icon path of the course.
     */
    courseIconPath: string;

    /**
     * The published URL to the content item in the course.
     */
    contentUrl: string;
};

/**
 * A search result for curated indexed content.
 */
type SearchResult = {
    /**
     * The result content item.
     */
    item: CurationNode | LearnableContent;

    /**
     * A published path to the content item in a course curated for the
     * request KA locale and region.
     */
    path: ContentPathInCourse;
};

/**
 * A hook to query for content indexed for search. Returning content appearing
 * in the courses-menu curation for the page KA locale and region first in the
 * results order.
 *
 * @param query The search query.
 * @param allowedAncestorSlugs The curation nodes ancestors slugs to filter by.
 * @param contentKinds The content kinds to filter by.
 * @param pageNum The page of results to return.
 */
export function useCuratedIndexedContentSearch(
    query: string,
    allowedAncestorSlugs: Array<string>,
    contentKinds: Array<string>,
    pageNum: number,
): Result<Array<SearchResult>> {
    const coursesMenuResult = useLearnMenuTopicsQuery();

    const allowedSlugs = new Set(allowedAncestorSlugs);

    const curatedCourseIds = new Set<string>();
    // We collect all courses that are either specified as allowed ancestors
    // themselves, or are curated under allowed domains in the courses-menu.
    for (const domain of coursesMenuResult.domains) {
        const allowedDomain = allowedSlugs.has(domain.slug);

        for (const link of domain.children) {
            if (
                "courseId" in link &&
                link.courseId &&
                (allowedDomain || allowedSlugs.has(link.slug))
            ) {
                curatedCourseIds.add(link.courseId);
            }
        }
    }
    // The AI Activities course is a special case, it does not appear in any
    // courses-menu curation, but it's indexed for searching Khanmigo activites.
    if (allowedSlugs.has(AI_ACTIVITIES_SLUG)) {
        curatedCourseIds.add(AI_ACTIVITIES_ID);
    }

    // Search query is skipped while the courses-menu curation is loading.
    const queryOptions = coursesMenuResult.loading
        ? {skip: true}
        : {
              variables: {
                  query,
                  allowedTopicAncestors: allowedAncestorSlugs,
                  contentKinds,
                  numResults: QUERY_SEARCH_RESULTS,
                  // We're querying for multiple pages worth of search results
                  // at a time, but only displaying a single page to the user.
                  // As the user navigates to next _display_ pages, we'll only
                  // advance the _query_ page when reaching the end of results
                  // pages we already queried for - querying for the next set
                  // of pages worth of results.
                  pageNum: Math.floor(pageNum / NUM_QUERY_PAGES),
              },
              fetchPolicy: FetchPolicy.CacheBeforeNetwork,
          };

    const [searchResult] = useQuery(INDEXED_CONTENT_SEARCH_QUERY, queryOptions);

    if (searchResult.status !== "success") {
        return searchResult;
    }

    const indexedResults = searchResult.data.searchPage.results;

    if (!indexedResults) {
        return {
            status: "error",
            error: new KAError(
                "Unexpected null searchPage results",
                Errors.GraphqlResponse,
            ),
        };
    }

    // We want to pick a published path that is within a course that is found
    // in the courses-menu curation, and ideally one that is also curriculum
    // aligned. Otherwise, pick the first published path, which is canonical.
    const pathCompare = (a: ContentPathInCourse, b: ContentPathInCourse) => {
        const aCurated = curatedCourseIds.has(a.courseId);
        const bCurated = curatedCourseIds.has(b.courseId);

        if (aCurated !== bCurated) {
            return Number(bCurated) - Number(aCurated);
        }

        // The AI Activities course is a special case, it exists to serve
        // all curriculums.
        return (
            Number(
                b.courseId === AI_ACTIVITIES_ID ||
                    b.courseCurriculum === RequestInfo.curriculum,
            ) -
            Number(
                a.courseId === AI_ACTIVITIES_ID ||
                    a.courseCurriculum === RequestInfo.curriculum,
            )
        );
    };

    const pageResults = indexedResults
        .map((result) => {
            if (!result) {
                return null;
            }

            const {curationNode, learnableContent} = result;

            if (curationNode) {
                const paths = getContentPathsInCourses(curationNode.topicPaths);

                if (!paths.length) {
                    Log.error(
                        "Unexpected search result for curation node with no published path",
                        Errors.Internal,
                        {
                            metadata: {
                                query,
                                kaLocale: RequestInfo.kaLocale,
                                kaRegion: RequestInfo.kaRegion,
                                curriculum: RequestInfo.curriculum,
                                contentId: curationNode.id,
                                contentKind: curationNode.contentKind,
                            },
                        },
                    );

                    return null;
                }

                // Curation nodes only have a single published path.
                const path = paths[0];

                return {item: curationNode, path};
            }

            if (learnableContent) {
                const paths = getContentPathsInCourses(
                    learnableContent.topicPaths,
                );

                if (!paths.length) {
                    Log.error(
                        "Unexpected search result for learnable content with no published paths",
                        Errors.Internal,
                        {
                            metadata: {
                                query,
                                kaLocale: RequestInfo.kaLocale,
                                kaRegion: RequestInfo.kaRegion,
                                curriculum: RequestInfo.curriculum,
                                contentId: learnableContent.id,
                                contentKind: learnableContent.contentKind,
                            },
                        },
                    );

                    return null;
                }

                // Learnable content can have many published paths.
                const path = paths.sort(pathCompare)[0];

                return {item: learnableContent, path};
            }

            return null;
        })
        .filter(isTruthy)
        .sort((a, b) => pathCompare(a.path, b.path))
        .slice(
            (pageNum % NUM_QUERY_PAGES) * NUM_RESULTS_PER_PAGE,
            ((pageNum % NUM_QUERY_PAGES) + 1) * NUM_RESULTS_PER_PAGE,
        );

    return {status: "success", data: pageResults};
}

/**
 * Transform topic-paths for a Course, Unit, Lesson, or Learnable item
 * to paths in courses in which it is published.
 */
function getContentPathsInCourses(
    topicPaths: CurationNode["topicPaths"] | LearnableContent["topicPaths"],
): Array<ContentPathInCourse> {
    if (!topicPaths) {
        return [];
    }

    return topicPaths
        .map((topicPath) => {
            const path = topicPath?.path;
            // We expect paths for a Course, Unit, Lesson, or Learnable nodes.
            if (!path || path.length < 2) {
                return null;
            }

            const domainNode = path[0]?.content;
            // The first node in the path is always a Domain in DCUL strcuture.
            if (!domainNode || domainNode.__typename !== "Topic") {
                return null;
            }

            const courseNode = path[1]?.content;
            // The second node in the path is always a Course in DCUL structure.
            if (!courseNode || courseNode.__typename !== "Topic") {
                return null;
            }

            let contentUrl = "";

            for (const segment of path) {
                if (!segment) {
                    return null;
                }

                switch (segment.kind) {
                    case SearchableContentKinds.AIActivity:
                        contentUrl += "/ai/" + segment.slug;
                        break;
                    case SearchableContentKinds.Article:
                        contentUrl += "/a/" + segment.slug;
                        break;
                    case SearchableContentKinds.Exercise:
                        contentUrl += "/e/" + segment.slug;
                        break;
                    case SearchableContentKinds.Video:
                        contentUrl += "/v/" + segment.slug;
                        break;
                    default:
                        contentUrl += "/" + segment.slug;
                        break;
                }
            }

            return {
                domainIconPath: domainNode.iconPath ?? "",
                courseId: courseNode.id,
                courseTitle: courseNode.translatedTitle ?? "",
                courseCurriculum: courseNode.curriculumKey ?? "",
                courseIconPath: courseNode.iconPath ?? "",
                contentUrl,
            };
        })
        .filter(isTruthy);
}
